import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { resolveTarget } from './semanticTargets';
import { coachApi, emitCoachEvent } from './coachApi';

// Anchored highlight for the current Coach step. The visible ring is painted
// DIRECTLY on the target element via box-shadow (not a coordinate-positioned
// overlay), so it stays perfectly aligned on every device — desktop, tablet and
// mobile — where a `position: fixed` overlay would drift because the visual and
// layout viewports differ. Safe actions only (style + scroll) — never clicks.
// The applied style is saved and fully restored on change/unmount.
const RING = '0 0 0 2px rgb(139,92,246), 0 0 0 6px rgba(139,92,246,0.28)';
const DEFAULT_ARROW = { color: '#ef4444', bounce: true };

// Tenant arrow style is fetched once and shared across every HighlightLayer.
let _stylePromise = null;
function loadArrowStyle() {
  if (!_stylePromise) {
    _stylePromise = coachApi.config()
      .then((c) => (c && c.highlight_style) ? c.highlight_style : DEFAULT_ARROW)
      .catch(() => DEFAULT_ARROW);
  }
  return _stylePromise;
}

export default function HighlightLayer({ targetId }) {
  const [box, setBox] = useState(null);
  const [arrow, setArrow] = useState(DEFAULT_ARROW);

  useEffect(() => { let m = true; loadArrowStyle().then((s) => { if (m) setArrow(s || DEFAULT_ARROW); }); return () => { m = false; }; }, []);

  useEffect(() => {
    if (!targetId) { setBox(null); return undefined; }
    let raf;
    let styled = null;   // element currently carrying the ring
    let prev = null;     // its saved inline style

    // Level 2 auto-advance: when the user actually CLICKS the highlighted control,
    // emit a trusted `control_ping` (capture phase, so it fires before a nav click
    // unmounts the element). A step whose completion event is `control_ping` then
    // advances on its own — the Coach follows the user, no manual "Next".
    const onHit = () => { try { emitCoachEvent('control_ping', targetId); } catch (e) { /* ignore */ } };

    const restore = () => {
      if (styled) {
        styled.removeEventListener('click', onHit, true);
        if (prev) {
          styled.style.boxShadow = prev.boxShadow;
          styled.style.borderRadius = prev.borderRadius;
          styled.style.transition = prev.transition;
          styled.removeAttribute('data-coach-highlighted');
        }
      }
      styled = null; prev = null;
    };

    const tick = () => {
      const el = resolveTarget(targetId);
      if (el) {
        if (el !== styled) {
          restore();
          styled = el;
          prev = { boxShadow: el.style.boxShadow, borderRadius: el.style.borderRadius, transition: el.style.transition };
          el.style.transition = 'box-shadow 140ms ease';
          if (!el.style.borderRadius) el.style.borderRadius = '10px';
          el.style.boxShadow = RING;
          el.setAttribute('data-coach-highlighted', 'true');
          el.addEventListener('click', onHit, true);
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const r = el.getBoundingClientRect();
        setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        restore();
        setBox(null);
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); restore(); };
  }, [targetId]);

  // A bouncing RED arrow points at the highlighted button so it clearly stands
  // out. It tracks the live target box (re-measured every frame above), so it
  // never drifts. When there is no room above the button it flips to below.
  if (!box) return null;
  const above = box.top > 60;
  const arrowLeft = box.left + box.width / 2;
  const arrowTop = above ? box.top - 46 : box.top + box.height + 10;
  return (
    <>
      <div
        data-testid="coach-highlight"
        aria-hidden="true"
        style={{
          position: 'fixed', top: box.top, left: box.left,
          width: box.width, height: box.height,
          pointerEvents: 'none', zIndex: 60, opacity: 0,
        }}
      />
      <div
        data-testid="coach-arrow"
        aria-hidden="true"
        style={{ position: 'fixed', top: arrowTop, left: arrowLeft, zIndex: 70, pointerEvents: 'none' }}
      >
        <div style={{ transform: 'translateX(-50%)' }}>
          {above
            ? <ArrowDown className={`w-8 h-8${arrow.bounce ? ' animate-bounce' : ''}`} strokeWidth={3} style={{ color: arrow.color || DEFAULT_ARROW.color, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))' }} />
            : <ArrowUp className={`w-8 h-8${arrow.bounce ? ' animate-bounce' : ''}`} strokeWidth={3} style={{ color: arrow.color || DEFAULT_ARROW.color, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))' }} />}
        </div>
      </div>
    </>
  );
}
